'use client';

import { useState } from 'react';
import { Flag } from 'lucide-react';

import {
  getStoreReportCategories,
  getStoreReportOwnership,
  isStoreReportVerticalKey,
  type StoreReportCategory,
  type StoreReportVerticalKey,
} from '@/types/storeReport';

/**
 * Shopper-facing "Report a problem" control. Reports are captured and the admin
 * is alerted; they never automatically freeze the store.
 *
 * Two-tier ownership drives the copy: marketplace concerns are reviewed by
 * Zamzi; commercial concerns are passed to the owner to settle directly.
 */
export const StoreReportDialog = ({
  profileId,
  shopName,
  vertical = 'retail',
  label = 'Report this shop',
}: {
  profileId: string;
  shopName: string;
  vertical?: StoreReportVerticalKey;
  label?: string;
}) => {
  const categories = getStoreReportCategories(vertical);
  const noun = isStoreReportVerticalKey(vertical) ? vertical : 'other';
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<StoreReportCategory>(categories[0].value);
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      const res = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profileId,
          category,
          description,
          reporterName: name || undefined,
          reporterEmail: email || undefined,
          reporterPhone: phone || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not send your report.');
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not send your report.');
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
      >
        <Flag className="h-4 w-4 text-red-500" aria-hidden="true" />
        {label}
      </button>
    );
  }

  if (status === 'sent') {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <p className="font-semibold">Thank you — your report has been sent.</p>
        <p className="mt-1 text-emerald-800">Our team will review it. You may be contacted if we need more details.</p>
      </div>
    );
  }

  const isMarketplace = getStoreReportOwnership(vertical, category) === 'marketplace';

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm">
      <h3 className="text-sm font-bold text-slate-900">Report a problem with {shopName}</h3>
      <p className="mt-1 text-xs text-slate-500">
        This does not automatically change the {noun}. {isMarketplace
          ? 'Our team reviews these reports.'
          : 'We share this with the owner so you can sort it out directly.'}
      </p>

      <label className="mt-3 block text-xs font-medium text-slate-700">
        What went wrong?
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as StoreReportCategory)}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>

      <label className="mt-3 block text-xs font-medium text-slate-700">
        Tell us what happened
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          minLength={10}
          maxLength={2000}
          rows={4}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder={`Include any order details, dates and what the ${noun} said.`}
        />
      </label>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional)"
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone (optional)"
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
      </div>
      <p className="mt-1 text-[11px] text-slate-400">
        Add the email you used to order so we can verify you as a buyer.
      </p>

      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={status === 'sending' || description.trim().length < 10}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {status === 'sending' ? 'Sending…' : 'Send report'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};
